/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import './style.scss';

import { COUNCIL_STATUS, COUNCIL_TYPE_CODE, currencyOptions, TIMEZONE_VN, TOAST } from 'constant';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFileName, getAcadamicLevelShortHand } from 'utils/func';
import { useGetCouncilReview } from 'utils/hooks/councils';
import { useGetAllOrganizations } from 'utils/hooks/organizations';
import { useMutation } from 'react-query';
import { downloadFile } from 'apis/councils.api';
import { toast } from 'layout';
import { useGetLatestVersion } from 'utils/hooks/projectProposals';
import Members from './Members';

export default function GeneralCouncilInfo({ councilId, projectProposalId }) {
  const { t } = useTranslation();
  const { data: councilData } = useGetCouncilReview(councilId, projectProposalId, {
    enabled: !!councilId && !!projectProposalId,
  });

  const latestVersion = useGetLatestVersion(projectProposalId, {
    enabled: !!projectProposalId,
  });

  const { mutateAsync, isLoading } = useMutation(downloadFile);

  const handleDownloadFile = async (path) => {
    try {
      const response = await mutateAsync({ councilId, path });
      const file = new Blob([response.data]);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = formatFileName(path);

      link.click();
    } catch (error) {
      toast(TOAST.ERROR, t('common.message.downloadFileFailed'));
    }
  };

  const [owner, projectProposal, councilTypeCode] = useMemo(
    () => [
      councilData?.projectProposalCouncils?.[0]?.projectProposal?.projectProposalContacts?.[0] ??
        {},
      councilData?.projectProposalCouncils?.[0]?.projectProposal ?? {},
      councilData?.councilType?.code || '',
    ],
    [councilData]
  );

  const isShowScore = useMemo(
    () =>
      councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL ||
      councilTypeCode === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL ||
      councilTypeCode === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL,
    [councilTypeCode]
  );

  const { data: organizations, isLoading: isLoadingOrganizations } = useGetAllOrganizations({});

  const renderCouncilName = (isHeader) => {
    if (councilTypeCode === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      return t(`council.${isHeader ? 'viewList' : 'info'}.preliminarySelectionCouncil`);
    }
    if (councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      if (projectProposal?.shortenedReview) {
        return t(`council.${isHeader ? 'viewList' : 'info'}.specializationAndFinanceCouncil`);
      }
      return t(`council.${isHeader ? 'viewList' : 'info'}.specializationCouncil`);
    }

    if (councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      return t(`council.${isHeader ? 'viewList' : 'info'}.financeCouncil`);
    }

    if (councilTypeCode === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      return t(`council.${isHeader ? 'viewList' : 'info'}.midtermReportCouncil`);
    }

    if (councilTypeCode === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      return t(`council.${isHeader ? 'viewList' : 'info'}.acceptanceCouncil`);
    }

    if (councilTypeCode === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      return t(`council.${isHeader ? 'viewList' : 'info'}.liquidationCouncil`);
    }

    return null;
  };
  return (
    <div className="m-0 bg-white p-5 p-fluid detail-info-council">
      <div className="flex flex-column mb-5">
        <h3
          className="line-height-3 text-4xl text-center font-semibold"
          style={{ marginBottom: '0px', paddingBottom: '1px' }}
        >
          {renderCouncilName(true)?.toUpperCase()}
        </h3>
      </div>

      <div className="ml-0 sm:ml-0 md:ml-4 lg:ml-4">
        <div className="pb-2">
          {/* Name and owner of project proposal */}
          <div className="text-lg mb-3">
            <h4 className="mb-3">{t('council.view.overviewLabel').toUpperCase()}</h4>
            <div className="ml-4 grid">
              <div className="col-12">
                <span className="font-semibold">
                  {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
                </span>
                <span>{latestVersion?.vietnameseName}</span>
              </div>
              <div className="col-12">
                <span className="font-semibold">
                  {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
                </span>
                <span>{latestVersion?.englishName}</span>
              </div>
              <div className="col-12">
                <span className="font-semibold">
                  {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
                </span>
                <span>
                  {getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)}
                  {owner?.fullname ?? ''}
                </span>
              </div>
            </div>
          </div>

          {/* Council info */}
          <div className="text-lg mb-3">
            <h4 className="mb-3">B. {renderCouncilName(false)?.toUpperCase()}</h4>
            <div className="ml-4 mb-2 grid">
              <div className="col">
                <span className="font-semibold">{t('council.fields.name')}: </span>
                <span className="w-full">{councilData?.name}</span>
              </div>
            </div>
            <div className="ml-4 grid">
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">{t('council.fields.establishDate')}: </span>
                <span>
                  {councilData?.timeCreated &&
                    new Date(councilData?.timeCreated).toLocaleDateString('en-GB', {
                      timeZone: TIMEZONE_VN,
                    })}
                </span>
              </div>
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">{t('council.fields.decisionToEstablish')}: </span>
                {councilData?.projectProposalCouncils?.[0]?.establishmentFileUrl && (
                  <span
                    className="p-link text-primary underline"
                    onClick={() => {
                      handleDownloadFile(
                        councilData?.projectProposalCouncils?.[0]?.establishmentFileUrl
                      );
                    }}
                  >
                    {t('council.view.decision')}
                  </span>
                )}
              </div>
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
                {councilData?.projectProposalCouncils?.[0]?.reviewDeadline &&
                  new Date(
                    councilData?.projectProposalCouncils?.[0]?.reviewDeadline
                  ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
              </div>
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">{t('council.fields.meetingDate')}: </span>
                <span>
                  {councilData?.projectProposalCouncils?.[0]?.timeMet &&
                    new Date(councilData?.projectProposalCouncils?.[0]?.timeMet).toLocaleDateString(
                      'en-GB',
                      { timeZone: TIMEZONE_VN }
                    )}
                </span>
              </div>
              {isShowScore && (
                <>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.totalScore')}: </span>
                    <span>{councilData?.projectProposalCouncils?.[0]?.scoreTotal}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.averageScore')}: </span>
                    <span>{councilData?.projectProposalCouncils?.[0]?.scoreAverage}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                    <span className="font-semibold">{t('council.fields.classification')}: </span>
                    <span>
                      {`${t(
                        `council.viewList.${councilData?.projectProposalCouncils?.[0]?.classification}`
                      )}`}
                    </span>
                  </div>
                </>
              )}

              {councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL && (
                <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                  <span className="font-semibold">{t('council.fields.approvedBudget')}: </span>
                  <span>
                    {councilData?.projectProposalCouncils?.[0]?.approvedBudget
                      ? parseInt(
                          councilData?.projectProposalCouncils?.[0]?.approvedBudget ?? 0,
                          10
                        )?.toLocaleString('vi', currencyOptions)
                      : ''}
                  </span>
                </div>
              )}
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">{t('council.fields.result')}: </span>
                <span>
                  {councilData?.projectProposalCouncils?.[0]?.councilStatus !==
                  COUNCIL_STATUS.QUALIFIED ? (
                    councilData?.projectProposalCouncils?.[0]?.councilStatus ===
                    COUNCIL_STATUS.NOT_QUALIFIED ? (
                      <span className="text-success">{t('council.viewList.notQualified')}</span>
                    ) : (
                      <span className="text-danger">{t('council.viewList.notReviewed')}</span>
                    )
                  ) : (
                    <span className="text-danger">{t('council.viewList.qualified')}</span>
                  )}
                </span>
              </div>
              {/* <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">
                  {t('council.fields.numberOfMembersReviewed')}:{' '}
                </span>
                <span>
                  {getNumberMembersReviewed(council?.councilMembers, resultStatus)}/
                  {getFilterMembersAreAllowedReview(council?.councilMembers)?.length}
                </span>
              </div>
              <div className="col-12 sm:col-6 md:col-6 lg:col-6">
                <span className="font-semibold">
                  {t('council.fields.currentTotalScoreReviewed')}:{' '}
                </span>
                <span>{sumScoreInputFormOfAllMember(council?.councilMembers, resultStatus)}</span>
              </div> */}

              {/* Council members */}
            </div>
            <div className="col-12">
              <h4 className="mb-3">{t('council.view.councilMemberLabel')}</h4>
              <div className="ml-4">
                {!(isLoading || isLoadingOrganizations) && (
                  <Members
                    council={councilData}
                    members={councilData?.councilMembers}
                    councilType={councilData?.councilType}
                    organizations={organizations}
                    projectProposalId={projectProposalId}
                    councilId={councilData?.id}
                    reviewDeadline={councilData?.projectProposalCouncils?.[0]?.reviewDeadline}
                    isShowScore={isShowScore}
                    isShowValidScore
                    yearProposed={parseInt(councilData?.yearProposed, 10) || null}
                    template={parseInt(councilData?.projectProposal?.template, 10)}
                    hasProjects={false}
                    isShowStatus
                    isShowResult
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

GeneralCouncilInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  councilId: PropTypes.string,
  projectProposalId: PropTypes.string,
};

GeneralCouncilInfo.defaultProps = {
  councilId: '',
  projectProposalId: '',
};
